import { css } from '@styled-system/css';
import { token } from '@styled-system/tokens';
import type { SystemProperties } from '@styled-system/types';

import { DownloadIcon } from '@/shared/components/ui/Logo/DownloadIcon';

import { AccountIcon } from './AccountIcon';
import { AddIcon } from './AddIcon';
import { ArrowIcon } from './ArrowIcon';
import { ArrowRightIcon } from './ArrowRightIcon';
import { BackIcon } from './BackIcon';
import { BasketIcon } from './BasketIcon';
import { BellIcon } from './BellIcon';
import { BlitzIcon } from './BlitzIcon';
import { CheckIcon } from './CheckIcon';
import { CloseIcon } from './CloseIcon';
import { CrownIcon } from './CrownIcon';
import { CubeIcon } from './CubeIcon';
import { DiscordIcon } from './DiscordIcon';
import { ErrorIcon } from './ErrorIcon';
import { EuroIcon } from './EuroIcon';
import { ExternalLinkIcon } from './ExternalLinkIcon';
import { FilterIcon } from './FilterIcon';
import { FolderIcon } from './FolderIcon';
import { GlobeIcon } from './GlobeIcon';
import { InfoIcon } from './InfoIcon';
import { LogoutIcon } from './LogoutIcon';
import { MessageIcon } from './MessageIcon';
import { ParamIcon } from './ParamIcon';
import { PaymentIcon } from './PaymentIcon';
import { PlusIcon } from './PlusIcon';
import { RenameIcon } from './RenameIcon';
import { SearchIcon } from './SearchIcon';
import { SettingsIcon } from './SettingsIcon';
import { StarIcon } from './StarIcon';
import { StopIcon } from './StopIcon';
import { TagIcon } from './TagIcon';
import { TrashIcon } from './TrashIcon';
import type { LogoVariant } from './variants';

interface LogoProps {
  variant: LogoVariant;
  className?: string;
  color?: SystemProperties['color'];
  /** Icon size in px. Defaults to 10 to preserve legacy callers. */
  size?: number;
}

const iconComponents = {
  account: AccountIcon,
  plus: PlusIcon,
  trash: TrashIcon,
  rename: RenameIcon,
  stop: StopIcon,
  arrow: ArrowIcon,
  'arrow-right': ArrowRightIcon,
  back: BackIcon,
  add: AddIcon,
  bell: BellIcon,
  check: CheckIcon,
  info: InfoIcon,
  blitz: BlitzIcon,
  error: ErrorIcon,
  euro: EuroIcon,
  close: CloseIcon,
  crown: CrownIcon,
  filter: FilterIcon,
  folder: FolderIcon,
  param: ParamIcon,
  logout: LogoutIcon,
  search: SearchIcon,
  settings: SettingsIcon,
  tag: TagIcon,
  basket: BasketIcon,
  cube: CubeIcon,
  globe: GlobeIcon,
  star: StarIcon,
  message: MessageIcon,
  discord: DiscordIcon,
  'external-link': ExternalLinkIcon,
  payment: PaymentIcon,
  download: DownloadIcon,
} as const;

export const Logo = ({ variant, className, color, size = 10 }: LogoProps) => {
  const IconComponent = iconComponents[variant];

  if (!IconComponent) {
    return null;
  }

  const resolvedColor = color
    ? typeof color === 'string' && color.includes('.')
      ? token.var(`colors.${color}` as any)
      : color
    : undefined;

  const logoStyles = css({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  });

  return (
    <div className={`${logoStyles} ${className || ''}`} style={{ width: `${size}px`, height: `${size}px` }}>
      <IconComponent color={typeof resolvedColor === 'string' ? resolvedColor : undefined} />
    </div>
  );
};

// Re-export all icon components and types
export {
  AccountIcon,
  PlusIcon,
  TrashIcon,
  RenameIcon,
  StopIcon,
  ArrowIcon,
  ArrowRightIcon,
  BackIcon,
  AddIcon,
  BellIcon,
  CheckIcon,
  InfoIcon,
  BlitzIcon,
  ErrorIcon,
  EuroIcon,
  CloseIcon,
  CrownIcon,
  FilterIcon,
  FolderIcon,
  ParamIcon,
  LogoutIcon,
  SearchIcon,
  SettingsIcon,
  TagIcon,
  BasketIcon,
  CubeIcon,
  GlobeIcon,
  StarIcon,
  MessageIcon,
  DiscordIcon,
  ExternalLinkIcon,
  PaymentIcon,
};
export type { LogoProps };
export type { LogoVariant } from './variants';
